import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Tabs, Typography } from 'iglooform';
import { Spin } from 'antd';
import { ROLE } from '@/utils/role';
import Context from '@/utils/context';
import Overview from './overview';
import Upline from './upline';
import Downline from './downline';
import styles from './index.less';
import throttle from 'lodash/throttle';
import { getAgencyInfo } from '../service';

const { TabPane } = Tabs;

const TABPANE_MAP_KEY = {
  Overview: 'Overview',
  Upline: 'Upline',
  Downlines: 'Downlines',
};

const BusinessManagementPage = () => {
  const { formatMessage } = useIntl();
  const { isPc } = useContext(Context);
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [loading, setLoading] = useState<boolean>(false);

  const handleGetAgencyInfo = throttle(
    async (): Promise<void> => {
      getAgencyInfo()
        .then((res) => {
          if (res) {
            setUserInfo(res.userInfo);
          }
        })
        .finally(() => setLoading(false));
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    handleGetAgencyInfo();
  }, []);

  const { role } = userInfo || {};

  // 角色菜单权限：
  // ADM: overview, down-lines
  // SBM: overview, up-line, down-line
  // Business Manager: overview, up-line, down-lines
  // Agent Head: overview, up-line, down-lines
  // Agent: up-line

  const roleMapTanPaneKey = {
    [ROLE.agencyDevelopmentManager]: [
      TABPANE_MAP_KEY.Overview,
      TABPANE_MAP_KEY.Downlines,
    ],
    [ROLE.sbm]: [
      TABPANE_MAP_KEY.Overview,
      TABPANE_MAP_KEY.Upline,
      TABPANE_MAP_KEY.Downlines,
    ],
    [ROLE.businessManager]: [
      TABPANE_MAP_KEY.Overview,
      TABPANE_MAP_KEY.Upline,
      TABPANE_MAP_KEY.Downlines,
    ],
    [ROLE.agentHead]: [
      TABPANE_MAP_KEY.Overview,
      TABPANE_MAP_KEY.Upline,
      TABPANE_MAP_KEY.Downlines,
    ],
    [ROLE.agent]: [TABPANE_MAP_KEY.Upline],
  };

  const tabPaneMap = {
    [TABPANE_MAP_KEY.Overview]: {
      title: formatMessage({ id: 'Overview' }),
      withTabPane: (
        <TabPane tab={formatMessage({ id: 'Overview' })} key="Overview">
          <Overview />
        </TabPane>
      ),
      dropTabPane: <Overview />,
    },
    [TABPANE_MAP_KEY.Upline]: {
      title: formatMessage({ id: 'Upline' }),
      withTabPane: (
        <TabPane tab={formatMessage({ id: 'Upline' })} key="Upline">
          <Upline />
        </TabPane>
      ),
      dropTabPane: <Upline />,
    },
    [TABPANE_MAP_KEY.Downlines]: {
      title: formatMessage({ id: 'Downlines' }),
      withTabPane: (
        <TabPane tab={formatMessage({ id: 'Downlines' })} key="Downlines">
          <Downline />
        </TabPane>
      ),
      dropTabPane: <Downline />,
    },
  };

  const renderTabPane = () => {
    if (role) {
      const tanPaneAuth = roleMapTanPaneKey[role] || [];
      // 只有一个tab页的，就不渲染上面的tab切换栏
      if (tanPaneAuth.length === 1) {
        return (
          <>
            <Typography level="h2" className={styles.oneTabTitle}>
              {tabPaneMap[tanPaneAuth[0]].title}
            </Typography>
            {tabPaneMap[tanPaneAuth[0]].dropTabPane}
          </>
        );
      }

      return (
        <Tabs type="line">
          {tanPaneAuth.map((item) => {
            return tabPaneMap[item].withTabPane;
          })}
        </Tabs>
      );
    }
    return null;
  };

  return (
    <Spin spinning={loading}>
      <div
        className={
          isPc
            ? styles.businessManagementContainer
            : styles.businessManagementContainerH5
        }
      >
        {renderTabPane()}
      </div>
    </Spin>
  );
};

export default BusinessManagementPage;
